import { useMemo } from 'react';

import { type Asset } from 'contentful';

import { type CloudinaryTransformConfig, getCloudinaryUrl } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { type BaseProduct, type CarouselItems } from '../../ProductDetails.types';

// ─────────────────────────────────────────────────────────────────────────────

export const useProductDetailsCarouselItems = (params: Params) => {
  const { baseProduct, carousel } = params;

  const carouselVariation = useFeatureFlag('CELS-4498-product-details-carousel-variation'); // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────────────

  const carouselItems = useMemo<CarouselItems>(() => {
    const baseProductImage: CarouselItems[number] = {
      type: 'image',
      source: getCloudinaryUrl(baseProduct.asset.url, BASE_PRODUCT_IMAGE_TRANSFORM_CONFIG), // prettier-ignore
      alt: baseProduct.name,
      contentFit: 'contain',
    };
    const otherItems = carousel.map(transformAssetToCarouselItem);

    if (carouselVariation === 'BASE_IMAGE_LAST') {
      return [...otherItems, baseProductImage];
    }

    if (carouselVariation === 'HIDE_BASE_IMAGE') {
      return otherItems;
    }

    return [baseProductImage, ...otherItems];
  }, [baseProduct, carousel, carouselVariation]);

  // ───────────────────────────────────────────────────────────────────────────

  return {
    carouselItems,
    carouselVariation,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function transformAssetToCarouselItem(asset: {
  fields: { imageVideo: Asset; staticImage?: Asset };
}): CarouselItems[number] {
  const media = asset.fields.imageVideo;
  const isVideo = media.fields.file.contentType.startsWith('video');
  const source = media.fields.file.url;
  const staticSource = asset.fields.staticImage?.fields?.file?.url;
  const alt = media.fields.title ?? media.fields.description;
  const type = isVideo ? 'video' : 'image';

  return { type, source, staticSource, alt };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const BASE_PRODUCT_IMAGE_TRANSFORM_CONFIG: CloudinaryTransformConfig = [
  { crop: 'crop', height: 0.95, width: 0.7 },
  { width: 1000 },
];

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  baseProduct: BaseProduct;
  carousel: { fields: { imageVideo: Asset; staticImage?: Asset } }[];
};
